import React from 'react';

import { classNames } from '@/helpers/classNames';
import constants from '@/theme/exportedVariables.module.scss';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Tooltip, useMediaQuery } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import styles from './FeedbackScale.module.scss';

const ChartTooltip = withStyles({
	tooltip: {
		backgroundColor: '#ffffff',
		color: '#313a4e',
		padding: '6px 10px',
		borderRadius: '5px',
		boxShadow: '0 0 8px rgba(49, 58, 78, 0.2)',
		fontSize: '12px',
	},
})(Tooltip);

const tooltipPopperProps = {
	modifiers: { offset: { offset: '0, -10' }, flip: { enabled: false } },
};

type StepOnChangeCallback = (value: number) => void;

/**
 * start, end and position props are capped from 1 to 5.
 */
export type FeedbackScaleProps = {
	className?: string;
	start?: number;
	end?: number;
	position?: number;
	stepLabelMap?: string[];
	onChange?: StepOnChangeCallback;
	size?: 'sm' | 'md';
	myPosition?: number;
	myPositionContent?: React.ReactNode;
	hasHover?: boolean;
	emojiPlacement?: 'overlay' | 'below';
};

export const FeedbackScale: React.FC<FeedbackScaleProps> = ({
	className,
	start = 1,
	end = 1,
	position = 1,
	onChange,
	stepLabelMap,
	size = 'md',
	myPosition,
	myPositionContent,
	hasHover = false,
	emojiPlacement = 'below',
}) => {
	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet}`);
	const classes = classNames([
		styles['root'],
		styles[`size-${size}`],
		emojiPlacement === 'overlay' && styles['emoji-overlay'],
		className,
	]);
	const startClamped = Math.round(clamp(start));
	const endClamped = Math.round(clamp(end));
	const positionClamped = clamp(position);

	const positionOffsetPercentage = ((positionClamped - 1) / 4) * 100;
	const startOffsetPercentage = ((startClamped - 1) / 4) * 100;
	const overlayWidthPercentage =
		((endClamped - 1) / 4) * 100 - startOffsetPercentage;

	const overlayMousePos = React.useRef({ x: 0, y: 0 });
	const overlayPopperRef = React.useRef<any>(null);
	const overlayVirtualEl = React.useRef({
		clientWidth: 0,
		clientHeight: 0,
		getBoundingClientRect: () => {
			const { x, y } = overlayMousePos.current;
			return {
				top: y,
				left: x,
				bottom: y,
				right: x,
				width: 0,
				height: 0,
				toJSON: () => ({}),
			} as DOMRect;
		},
	}).current;
	const handleOverlayMouseMove = (e: React.MouseEvent) => {
		overlayMousePos.current = { x: e.clientX, y: e.clientY };
		overlayPopperRef.current?.scheduleUpdate?.();
	};

	return (
		<div className={classes}>
			<div
				className={classNames([
					styles['steps'],
					isTablet && !!onChange && styles['hover'],
				])}
			>
				{[1, 2, 3, 4, 5].map((step) => (
					<Step
						key={`step-${step}`}
						value={step}
						onClick={(value) => {
							Haptics.impact({ style: ImpactStyle.Light });
							onChange && onChange(value);
						}}
					/>
				))}
			</div>
			<div className={styles['scale-limits']}>
				<ChartTooltip
					title="Typical feedback"
					placement="top"
					PopperProps={{
						...tooltipPopperProps,
						anchorEl: overlayVirtualEl,
						popperRef: overlayPopperRef,
					}}
				>
					<div
						className={styles['overlay']}
						style={{
							left: `${startOffsetPercentage}%`,
							width: `${overlayWidthPercentage}%`,
						}}
						onMouseMove={handleOverlayMouseMove}
					>
						<div className={styles['left']} />
						<div className={styles['middle']} />
						<div className={styles['right']} />
					</div>
				</ChartTooltip>
				{/* <ChartTooltip
					title="Team Avg."
					placement="top"
					PopperProps={tooltipPopperProps}
				> */}
				<div
					className={classNames([
						styles['position-indicator'],
						hasHover && styles['hover'],
					])}
					style={{ left: `${positionOffsetPercentage}%` }}
				>
					<div className={styles['position-line']} />
					<div className={styles['position-emoji']}>
						{getFeedbackStepDetails(positionClamped).emoji}
					</div>
				</div>
				{/* </ChartTooltip> */}
				{myPosition !== undefined && (
					<ChartTooltip
						title="You"
						placement="top"
						PopperProps={tooltipPopperProps}
					>
						<div
							className={styles['position-my-indicator']}
							style={{
								left: `${((clamp(myPosition) - 1) / 4) * 100}%`,
							}}
						>
							<div className={styles['position-my-line']} />
							{myPositionContent && (
								<div className={styles['position-my-avatar']}>
									{myPositionContent}
								</div>
							)}
						</div>
					</ChartTooltip>
				)}
			</div>
			<div className={styles['steps-background']} />
		</div>
	);
};

const Step: React.FC<{
	value: number;
	onClick?: StepOnChangeCallback;
}> = ({ value, onClick }) => {
	return (
		<div
			onTouchStart={() => {
				return !!onClick && onClick(value);
			}}
			onMouseDown={() => {
				return !!onClick && onClick(value);
			}}
			onClick={() => {
				return !!onClick && onClick(value);
			}}
		>
			{value}
		</div>
	);
};

const clamp = (val: number, min: number = 1, max: number = 5) =>
	Math.min(Math.max(val, min), max);

export const getFeedbackStepDetails = (
	val: number,
	stepLabelMap: string[] = [],
): { emoji: string; label: string } => {
	switch (Math.round(val)) {
		case 2:
			return {
				emoji: String.fromCodePoint(0x1f610),
				label: stepLabelMap[1] || 'Meh',
			};
		case 3:
			return {
				emoji: String.fromCodePoint(0x1f642),
				label: stepLabelMap[2] || 'Good',
			};
		case 4:
			return {
				emoji: String.fromCodePoint(0x1f600),
				label: stepLabelMap[3] || 'Great',
			};
		case 5:
			return {
				emoji: String.fromCodePoint(0x1f60d),
				label: stepLabelMap[4] || 'Amazing',
			};
		default:
			return {
				emoji: String.fromCodePoint(0x1f928),
				label: stepLabelMap[0] || 'Not at all',
			};
	}
};
